import Card from '@/components/Card';
import { Button } from '@/components/Button';
import { Input } from '@/components/Controls/Input';
import { Switch } from '@/components/Controls/Switch';
import { FormField } from '@/components/Form/FormField';
import { useEffect } from 'react';
import { FieldValues, Path } from 'react-hook-form';
import { FormManagementProps } from '@/type/form.types';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { useGetExistingRepairService } from '@/hooks/repair-service.hooks';
import { allFieldErrorsEmpty, resetFieldError } from '@/utils/object.utils';
import { FieldError } from '@/type/error.types';
import { Textarea } from '@/components/Controls/Textarea';

export const RepairServiceInfosManagement = <TSchema extends FieldValues>({
  t,
  data,
  watch,
  register,
  setValue,
  setError,
  errors,
  submitDisabled,
  onSubmit,
}: FormManagementProps<TSchema>) => {
  /**
   * === Set Value Watchers section ===
   */
  const nameValue = watch && watch('name' as Path<TSchema>);
  const activeValue = watch && watch('active' as Path<TSchema>);
  /**
   * === END OF Set Value Watchers section ===
   */

  const { data: existingRepairService } = useGetExistingRepairService({
    id: data?.id,
    name: nameValue,
  });

  /**
   * === Reset Error Messages On Value Change section ===
   */
  useEffect(() => {
    if (nameValue) {
      resetFieldError<TSchema>({ fieldName: 'name', setError });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nameValue]);
  /**
   * === END OF Reset Error Messages On Value Change section ===
   */

  /**
   * === Set Fields Initial Values section ===
   */
  useEffect(() => {
    if (setValue && data) {
      setValue('name' as Path<TSchema>, data.name ?? '');
      setValue('description' as Path<TSchema>, data.description ?? '');
      setValue('active' as Path<TSchema>, data.active);
    }
  }, [setValue, data]);
  /**
   * === END OF Set Fields Initial Values section ===
   */

  /**
   * === Handle Submit section ===
   */
  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const repairServiceErrorMessages =
      existingRepairService?.data?.errorMessages;

    if (repairServiceErrorMessages?.includes(FieldError.NAME_EXISTS)) {
      setError('name' as Path<TSchema>, {
        message: t('repairServiceNameAlreadyExists'),
      });
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    if (allFieldErrorsEmpty(errors)) {
      onSubmit?.();
    }
  };
  /**
   * === END OF Handle Submit section ===
   */

  return (
    <form onSubmit={handleSubmit}>
      <Card
        title={
          <TitleSection
            text={t('theRepairService')}
            defaultIconClassName="ki-badge"
          />
        }
        footer={
          <Button
            buttonClassName="px-8"
            text={
              submitDisabled ? `${t('common:loading')}...` : t('common:save')
            }
            disabled={submitDisabled}
          />
        }
        bodyClassName="py-8 flex flex-col gap-5"
      >
        <>
          <FormField
            text={t('repairServiceName')}
            required
            textWrapperClassName="mt-3 max-md:mt-0"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Input
                {...(register && register('name' as Path<TSchema>))}
                errorMessage={errors && (errors.name?.message as string)}
                placeholder={t('repairServiceName')}
                type="text"
              />
            </div>
          </FormField>
          <FormField text={t('common:description')} textWrapperClassName="mt-3">
            <div className="flex flex-1 flex-wrap items-start gap-5">
              <Textarea
                {...(register && register('description' as Path<TSchema>))}
                errorMessage={errors && (errors.description?.message as string)}
                placeholder={t('common:description')}
              />
            </div>
          </FormField>
          <FormField
            text={t('common:activeStatus')}
            textWrapperClassName="mt-3"
          >
            <div className="flex flex-1 flex-wrap items-start gap-5 mt-3">
              <Switch
                {...(register && register('active' as Path<TSchema>))}
                errorMessage={errors && (errors.active?.message as string)}
                checked={data ? activeValue : true}
              />
            </div>
          </FormField>
        </>
      </Card>
    </form>
  );
};
